/**
 * ETFDaireChat Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * Advanced AI-powered ETF Intelligence chat interface with comprehensive analysis capabilities.
 * Features include:
 * - Real-time ETF search and analysis with streaming responses
 * - Interactive ETF data tables with sorting, pagination, and detailed modals
 * - Professional quick actions for common ETF queries (Irish funds, ESG, costs, etc.)
 * - Full-screen mode with native browser API integration
 * - Advanced scroll management with respectful auto-scroll behavior
 * - Professional message rating system with toast notifications
 * - Comprehensive ETF data formatting and visualization
 * - Production-ready error handling and graceful degradation
 * - Speech recognition integration ready
 * - ETF metrics dashboard with real-time updates
 * - Document context awareness for specialized analysis
 * - Session management with token tracking
 * - Professional markdown rendering with syntax highlighting
 * - Responsive design with mobile-first approach
 * - ETF Screener data integration with proper attribution
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOnDairePage - Whether component is rendered on dedicated Daire page
 * @param {boolean} props.hideFullscreen - Whether to hide fullscreen toggle button
 * @param {function} props.onNavigateHome - Callback for home navigation
 * @param {function} props.onShowHelp - Callback for help display
 * @param {function} props.onWelcomeShown - Callback when welcome message is displayed
 * @param {Object} ref - Forward ref for parent component integration
 * @returns {JSX.Element} Comprehensive ETF Intelligence chat interface
 */
import React, { useState, useRef, useEffect, useLayoutEffect, forwardRef, useContext } from 'react';
import './ETFDaireChat.css';
import apiHelper from '../../utils/apiHelper';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { clearSessionContext } from '../../utils/chatStorage';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExpand, faCompress, faEraser, faStop, faExclamationCircle, faQuestionCircle, faTimes, faBolt } from '@fortawesome/free-solid-svg-icons';
import { toast, ToastContainer } from 'react-toastify';
import { AuthContext } from '../Auth/AuthContext';

/**
 * ETF Intelligence Quick Actions Configuration
 * 
 * Predefined quick action buttons for common ETF analysis queries.
 * Each action includes visual styling and optimized query text.
 */
// ETF Intelligence Quick Actions
const ETF_QUICK_ACTIONS = [
  {
    title: "Irish ETFs",
    icon: "🇮🇪",
    query: "Show me Irish-domiciled ETFs",
    color: "#28a745"
  },
  {
    title: "ESG Funds",
    icon: "🌱",
    query: "Find ESG ETFs with good sustainability ratings",
    color: "#20c997"
  },
  {
    title: "Lowest Costs",
    icon: "💰",
    query: "Show me the cheapest ETFs with lowest TER",
    color: "#fd7e14"
  },
  {
    title: "Largest Funds",
    icon: "📈",
    query: "Find the largest ETFs by assets under management",
    color: "#6f42c1"
  },
  {
    title: "Equity ETFs",
    icon: "📊",
    query: "Show me equity ETFs with good performance",
    color: "#007bff"
  },
  {
    title: "Bond ETFs",
    icon: "🏦",
    query: "Find fixed income and bond ETFs",
    color: "#dc3545"
  }
];

/**
 * ETFDaireChat Component
 * 
 * Main ETF Intelligence chat interface with comprehensive AI-powered analysis capabilities.
 * Integrates with backend ETF Intelligence API for real-time fund data and analysis.
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOnDairePage - Styling flag for dedicated Daire page rendering
 * @param {boolean} props.hideFullscreen - Controls fullscreen toggle visibility
 * @param {function} props.onNavigateHome - Handler for home navigation events
 * @param {function} props.onShowHelp - Handler for help system activation
 * @param {function} props.onWelcomeShown - Callback when welcome message is displayed
 * @param {Object} ref - Forward reference for parent component integration
 * @returns {JSX.Element} Complete ETF Intelligence chat interface
 */
const ETFDaireChat = forwardRef(function ETFDaireChat({ 
    isOnDairePage = false,
    hideFullscreen = false,
    onNavigateHome,
    onShowHelp,
    onWelcomeShown
}, ref) {
    const { user: [user] } = useContext(AuthContext);
  const chatWindowRef = useRef(null);
  const inputRef = useRef(null);
  
  //  ETF Intelligence State
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [abortController, setAbortController] = useState(null);
  const userWasAtBottomRef = useRef(true);
  const [selectedModel, setSelectedModel] = useState('gpt-4.1');
  const [maxDocs, setMaxDocs] = useState(10000); 
  
  //  ETF-specific state
  const [etfMetrics, setEtfMetrics] = useState({
    totalETFs: 0,
    filteredETFs: 0,
    avgTER: 0,
    totalAUM: 0
  });
  const [etfResults, setEtfResults] = useState([]);
  const [showETFResults, setShowETFResults] = useState(false);
  const [flashETFResultsButton, setFlashETFResultsButton] = useState(false);
  const lastETFResultsSignature = useRef('');
  
  //  ETF Table Pagination & Sorting State
  const [etfCurrentPage, setEtfCurrentPage] = useState(1);
  const [etfPageSize, setEtfPageSize] = useState(10);
  const [selectedETF, setSelectedETF] = useState(null);
  const [etfSortField, setEtfSortField] = useState('aum');
  const [etfSortDirection, setEtfSortDirection] = useState('desc');
  
  const [streamingState, setStreamingState] = useState({
    isSearching: false,
    documentsFound: 0,
    isAnalyzing: false,
    searchKeywords: []
  });

  const [currentMessageTokens, setCurrentMessageTokens] = useState(null);
  const [sessionTotalTokens, setSessionTotalTokens] = useState(0);
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [showQuickActionsInternal, setShowQuickActionsInternal] = useState(true);
  const [userScrolledUp, setUserScrolledUp] = useState(false);
  const isAutoScrollingRef = useRef(false);
  const [shouldShowWelcome, setShouldShowWelcome] = useState(true);
  const [messageRatings, setMessageRatings] = useState({});
  const bottomRef = useRef(null);
  const [isConversationMode] = useState(false);
  const ttsBufferRef = useRef('');

  // Initialize with ETF Intelligence welcome message
  useEffect(() => {
    if (user && messages.length === 0 && shouldShowWelcome) {
      const formattedName = user?.profile?.name ?
        user.profile.name.split(' ')[0].charAt(0).toUpperCase() + user.profile.name.split(' ')[0].slice(1) :
        'there';

      const welcomeMessage = {
        type: 'bot',
        content: `**Welcome to ETF Intelligence, ${formattedName}!** 

I'm Daire, your AI-powered ETF analyst, ready to help you explore and analyze exchange-traded funds with precision and insight.

**What I can help you with:**
- **ETF Analysis** - Detailed fund breakdowns and comparisons
- **Smart Filtering** - Find ETFs by domicile, asset class, ESG criteria
- **Cost Analysis** - Compare TERs and expense ratios
- **Performance Insights** - AUM trends and market positioning
- **Geographic Focus** - Irish-domiciled and European ETFs
- **ESG Intelligence** - Sustainable investing options

Ready to dive into the world of ETF intelligence? Ask me anything or try one of the quick actions below!`,
        isWelcomeMessage: true
      };

      setMessages([welcomeMessage]);
      if (onWelcomeShown) {
        onWelcomeShown();
      }
    }
  }, [user, messages.length, shouldShowWelcome, onWelcomeShown]);

  // ETF Intelligence Quick Actions Component
  const ETFQuickActions = () => {
    if (messages.length > 1 || !showQuickActionsInternal) return null;

    return (
      <div className={`suggestion-boxes etf-intelligence-actions${isOnDairePage ? ' is-on-daire-page' : ''}`}>
        {ETF_QUICK_ACTIONS.map((action, index) => (
          <Tippy
            key={index}
            content={action.query}
            placement="top"
            delay={[500, 0]}
          >
            <button
              className="suggestion-box etf-action"
              onClick={() => handleQuickAction(action.query)}
              style={{ borderColor: action.color }}
            >
              <span className="suggestion-icon etf-icon" style={{ color: action.color }}>
                {action.icon}
              </span>
              <span className="suggestion-text">{action.title}</span>
              <div className="etf-action-glow" style={{ backgroundColor: action.color + '20' }}></div>
            </button>
          </Tippy>
        ))}
      </div>
    );
  };

  // ETF Intelligence Handler
  const handleQuickAction = async (query) => {
    
    try {
      setIsProcessing(true);
      setEtfResults([]);
      setShowETFResults(false);
      
      // Reset scroll state at start of processing to ensure auto-scroll works
      setUserScrolledUp(false);
      
      // Also disable auto-scroll blocking temporarily
      isAutoScrollingRef.current = false;

      const authToken = await apiHelper.getAuthToken();
      const controller = new AbortController();
      setAbortController(controller);
    
      const newMessages = [...messages, { type: 'user', content: query }];
      setMessages(newMessages);

      // Force immediate scroll after adding user message
      setTimeout(() => {
        if (chatWindowRef.current) {
          isAutoScrollingRef.current = true; // Block scroll detection
          chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
          // Clear the flag after scroll completes
          setTimeout(() => {
            isAutoScrollingRef.current = false;
          }, 100);
        }
      }, 50);

      const apiUrl = process.env.REACT_APP_API_URL;
      
      // Always use ETF Intelligence endpoint
      const requestBody = {
        messages: newMessages.slice(-10).map(msg => ({
          role: msg.type === 'user' ? 'user' : 'assistant',
          content: msg.content
        })),
        userName: user?.profile?.name,
        userEmail: user?.profile?.email,
        model: selectedModel,
        maxDocs: maxDocs
      };

      const response = await fetch(`${apiUrl}daire/daireETFIntent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal
      });

      if (!response.ok) throw new Error('Network response was not ok');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let content = '';
      let buffer = '';

                  while (true) {
                const { value, done } = await reader.read();
                
                if (done) {
                    setIsProcessing(false);
                    setStreamingState(prev => ({
                        ...prev,
                        isSearching: false,
                        isAnalyzing: false,
                        documentsFound: 0
                    }));
                    if (isConversationMode && ttsBufferRef.current) {
                   
                        ttsBufferRef.current = '';
                    }
                    break;
                }

        const chunk = decoder.decode(value);
        buffer += chunk;

        let eventEnd;
        while ((eventEnd = buffer.indexOf('\n\n')) !== -1) {
          const event = buffer.slice(0, eventEnd);
          buffer = buffer.slice(eventEnd + 2);
          if (event.startsWith('data: ')) {
            const data = event.slice(6);
            if (data.trim() === '[DONE]') {
              setIsProcessing(false);
              setStreamingState(prev => ({
                ...prev,
                isSearching: false,
                isAnalyzing: false,
                documentsFound: 0
              }));
              break;
            }
            
                                    try {
                            const parsed = JSON.parse(data);

                            switch (parsed.type) {
                                case 'etf_results':
                                    setEtfResults(parsed.results || []);
                                    // Reset pagination to page 1 when new results arrive
                                    setEtfCurrentPage(1);
                                    // Reset scroll state when ETF results arrive to maintain auto-scroll
                                    if (isProcessing) {
                                        setUserScrolledUp(false);
                                    }
                                    break;

                                case 'etf_metrics':
                                    setEtfMetrics(parsed.metrics || {});
                                    // Reset scroll state when ETF metrics arrive to maintain auto-scroll
                                    if (isProcessing) {
                                        setUserScrolledUp(false);
                                    }
                                    break;

                case 'keywords':
                  setStreamingState(prev => ({
                    ...prev,
                    searchKeywords: parsed.keywords
                  }));
                  break;

                case 'document_count':
                  setStreamingState(prev => ({
                    ...prev,
                    isSearching: false,
                    documentsFound: parsed.count,
                    isAnalyzing: true
                  }));
                  break;

                                case 'content':
                                    content += parsed.content;
                                    // Reset scroll state for streaming content to maintain auto-scroll during processing
                                    if (isProcessing) {
                                        setUserScrolledUp(false);
                                    }
                                   
                                    setMessages(prev => {
                                        const updated = [...prev];
                                        const lastMessage = updated[updated.length - 1];
                                        if (lastMessage?.type === 'bot') {
                                            lastMessage.content = content;
                                        } else {
                                            updated.push({ type: 'bot', content: content });
                                        }
                                        return updated;
                                    });
                                    break;

               
                case 'usage_tracked':
                  setMessages(prev => {
                    const updated = [...prev];
                    const lastMessage = updated[updated.length - 1];
                    if (lastMessage?.type === 'bot') {
                      lastMessage.listId = parsed.listId;
                      lastMessage.totalTokens = parsed.totalTokens;
                    }
                    return updated;
                  });
                  if (parsed.totalTokens) {
                    setCurrentMessageTokens(parsed.totalTokens);
                    setSessionTotalTokens(prev => prev + parsed.totalTokens);
                  }
                  break;

                case 'error':
                  setMessages(prev => [...prev, {
                    type: 'error',
                    content: parsed.error || 'An error occurred while processing your ETF query.'
                  }]);
                  break;
              }


              if (parsed.content) {
                ttsBufferRef.current += parsed.content;
              }
                                    } catch (error) {
                            // If we get [DONE] but can't parse it, still finish
                            if (data.trim() === '[DONE]') {
                                setIsProcessing(false);
                                setStreamingState(prev => ({
                                    ...prev,
                                    isSearching: false,
                                    isAnalyzing: false,
                                    documentsFound: 0
                                }));
                            }
                        }
          }
        }
      }

    } catch (error) {
      // ETF Intelligence processing error handled gracefully
      setIsProcessing(false);
      setStreamingState(prev => ({
        ...prev,
        isSearching: false,
        isAnalyzing: false,
        documentsFound: 0
      }));
      
      if (error.name !== 'AbortError') {
        setMessages(prev => [...prev, {
          type: 'error',
          content: 'Sorry, there was an error processing your ETF intelligence request.'
        }]);
      }
    } finally {
      setIsProcessing(false);
      setStreamingState(prev => ({
        ...prev,
        isSearching: false,
        isAnalyzing: false,
        documentsFound: 0
      }));
      setAbortController(null);
      isAutoScrollingRef.current = false;
    }
  };

  // Handle regular submit (same as quick action for ETF Intelligence)
  const handleSubmit = async (e, overrideInput) => {
    if (e) e.preventDefault();
    const messageToSend = overrideInput !== undefined ? overrideInput : input;
    if (!messageToSend.trim() || isProcessing) return;

    setInput('');
      
    // Reset scroll state when starting new query to ensure auto-scroll works
    setUserScrolledUp(false);
    isAutoScrollingRef.current = false;
    
    // Route everything through ETF Intelligence
    await handleQuickAction(messageToSend);
  };

  // ETF Table Functions
  const handleETFPageChange = (newPage) => {
    const totalPages = Math.ceil(etfResults.length / etfPageSize);
    if (newPage >= 1 && newPage <= totalPages && newPage !== etfCurrentPage) {
      setEtfCurrentPage(newPage);
    }
  };

  // Page size dropdown removed - using fixed page size with sorting

  const handleETFRowClick = (etf) => {
    setSelectedETF(etf);
  };

  const closeETFModal = () => {
    setSelectedETF(null);
  };

  // ETF Sorting handlers
  const handleETFSort = (field) => {
    if (etfSortField === field) {
      // If clicking the same field, toggle direction
      setEtfSortDirection(etfSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // If clicking a new field, set it as sort field with ascending direction
      setEtfSortField(field);
      setEtfSortDirection('asc');
    }
    // Reset to page 1 when sorting changes
    setEtfCurrentPage(1);
  };

  // Get sort icon for table headers
  const getETFSortIcon = (field) => {
    if (etfSortField !== field) {
      return <i className="fas fa-sort daire-sort-icon inactive"></i>;
    }
    return etfSortDirection === 'asc' 
      ? <i className="fas fa-sort-up daire-sort-icon active"></i>
      : <i className="fas fa-sort-down daire-sort-icon active"></i>;
  };

  // Format functions for ETF data display
  const formatTER = (ter) => {
    if (!ter) return '-';
    const terFloat = parseFloat(ter.toString().replace('%', ''));
    return isNaN(terFloat) ? ter : `${terFloat.toFixed(2)}%`;
  };

  const formatAUM = (aum) => {
    if (!aum) return '-';
    const aumValue = parseFloat(aum.toString().replace(/[€,$MB\s]/g, ''));
    if (isNaN(aumValue)) return aum;
    
    if (aumValue >= 1000000000) {
      return `€${(aumValue / 1000000000).toFixed(2)}B`;
    } else if (aumValue >= 1000000) {
      return `€${(aumValue / 1000000).toFixed(0)}M`;
    } else {
      return `€${aumValue.toFixed(1)}M`;
    }
  };

  const formatDateIrish = (dateString) => {
    if (!dateString) return '-';
    
    let date;
    if (dateString.includes('-')) {
      const parts = dateString.split('-');
      if (parts[0].length === 4) {
        date = new Date(parts[0], parts[1] - 1, parts[2]);
      } else {
        date = new Date(parts[2], parts[0] - 1, parts[1]);
      }
    } else if (dateString.includes('/')) {
      date = new Date(dateString);
    } else {
      date = new Date(dateString);
    }
    
    if (isNaN(date.getTime())) {
      return dateString;
    }
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}/${month}/${year}`;
  };

  const formatSFDR = (sfdrValue) => {
    if (!sfdrValue) return '-';
    const sfdrStr = sfdrValue.toString().toUpperCase();
    
    if (sfdrStr.includes('NOT APPLICABLE')) return 'N/A';
    if (sfdrStr.includes('ARTICLE 6')) return 'Art 6';
    if (sfdrStr.includes('ARTICLE 8')) return 'Art 8';
    if (sfdrStr.includes('ARTICLE 9')) return 'Art 9';
    if (sfdrStr.includes('ARTICLE')) return sfdrStr.replace('ARTICLE', 'Art');
    
    return sfdrValue;
  };

  // ETF Results Display Component with Pagination & Modal
  const ETFResultsDisplay = () => {
    if (!showETFResults || etfResults.length === 0) return null;

    // Apply sorting to ETF results
    const sortedETFs = [...etfResults].sort((a, b) => {
      let aValue = a[etfSortField];
      let bValue = b[etfSortField];
      
      // Handle different data types
      if (etfSortField === 'fundTER' || etfSortField === 'aum') {
        // Convert to numbers for numerical sorting
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      } else if (typeof aValue === 'string' && typeof bValue === 'string') {
        // Case insensitive string comparison
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (aValue < bValue) return etfSortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return etfSortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    // Calculate pagination
    const totalPages = Math.ceil(sortedETFs.length / etfPageSize);
    const startIndex = (etfCurrentPage - 1) * etfPageSize;
    const endIndex = startIndex + etfPageSize;
    const displayedETFs = sortedETFs.slice(startIndex, endIndex);

    return (
      <div className="etf-results-container">
        <div className="etf-metrics-bar">
          <div className="etf-metric">
            <span className="metric-value">{etfMetrics.filteredETFs || etfResults.length}</span>
            <span className="metric-label">ETFs Found</span>
          </div>
          <div className="etf-metric">
            <span className="metric-value">{etfMetrics.avgTER ? `${etfMetrics.avgTER.toFixed(2)}%` : 'N/A'}</span>
            <span className="metric-label">Avg TER</span>
          </div>
          <div className="etf-metric">
            <span className="metric-value">{etfMetrics.totalAUM ? `€${(etfMetrics.totalAUM / 1e9).toFixed(1)}B` : 'N/A'}</span>
            <span className="metric-label">Total AUM</span>
          </div>
        </div>

        {/* Table Controls */}
        <div className="daire-etf-table-controls">
          <div className="daire-etf-table-info">
            Showing {etfResults.length === 0 ? 0 : startIndex + 1}-{Math.min(endIndex, etfResults.length)} of {etfResults.length} ETFs
            {totalPages > 1 && ` | Page ${etfCurrentPage} of ${totalPages}`}
          </div>
        </div>
        
        <div className="daire-etf-table-container">
          <div className="daire-etf-table-wrapper">
            <table className="daire-etf-table">
            <thead>
              <tr className="daire-etf-table-header">
                <th className="daire-sortable" onClick={() => handleETFSort('fundName')} title="Fund Name">
                  <span>FUND NAME</span>
                  {getETFSortIcon('fundName')}
                </th>
                <th className="daire-sortable" onClick={() => handleETFSort('fundDomicile')} title="Fund Domicile">
                  <span>DOMICILE</span>
                  {getETFSortIcon('fundDomicile')}
                </th>
                <th className="daire-sortable" onClick={() => handleETFSort('fundAssetClass')} title="Fund Asset Class">
                  <span>ASSET CLASS</span>
                  {getETFSortIcon('fundAssetClass')}
                </th>
                <th className="daire-sortable" onClick={() => handleETFSort('fundTER')} title="Total Expense Ratio">
                  <span>TER</span>
                  {getETFSortIcon('fundTER')}
                </th>
                <th className="daire-sortable" onClick={() => handleETFSort('aum')} title="Assets Under Management">
                  <span>AUM</span>
                  {getETFSortIcon('aum')}
                </th>
                <th className="daire-sortable" onClick={() => handleETFSort('fundESG')} title="ESG Classification">
                  <span>ESG</span>
                  {getETFSortIcon('fundESG')}
                </th>
              </tr>
            </thead>
            <tbody>
              {displayedETFs.map((etf, index) => (
                <tr key={index} className="daire-etf-table-row clickable-row" onClick={() => handleETFRowClick(etf)}>
                  <td className="daire-etf-fund-name" title={etf.fundName}>
                    {etf.fundName}
                  </td>
                  <td>
                    <span className={`daire-domicile-badge ${etf.fundDomicile?.toLowerCase() === 'ireland' ? 'ireland' : ''}`}>
                      {etf.fundDomicile}
                    </span>
                  </td>
                  <td>{etf.fundAssetClass}</td>
                  <td>{formatTER(etf.fundTER)}</td>
                  <td>{formatAUM(etf.aum)}</td>
                  <td>
                    <span className={`daire-esg-badge ${etf.fundESG?.toLowerCase() === 'yes' ? 'yes' : 'no'}`}>
                      {etf.fundESG}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="daire-etf-pagination">
            <button 
              className="daire-etf-pagination-btn"
              onClick={() => handleETFPageChange(1)}
              disabled={etfCurrentPage === 1}
              title="First Page"
            >
              <i className="fas fa-angle-double-left"></i>
            </button>
            <button 
              className="daire-etf-pagination-btn"
              onClick={() => handleETFPageChange(etfCurrentPage - 1)}
              disabled={etfCurrentPage === 1}
              title="Previous Page"
            >
              <i className="fas fa-angle-left"></i>
            </button>
            
            {/* Page numbers */}
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (etfCurrentPage <= 3) {
                pageNum = i + 1;
              } else if (etfCurrentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = etfCurrentPage - 2 + i;
              }
              
              return (
                <button
                  key={pageNum}
                  className={`daire-etf-pagination-btn ${pageNum === etfCurrentPage ? 'active' : ''}`}
                  onClick={() => handleETFPageChange(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}
            
            <button 
              className="daire-etf-pagination-btn"
              onClick={() => handleETFPageChange(etfCurrentPage + 1)}
              disabled={etfCurrentPage === totalPages}
              title="Next Page"
            >
              <i className="fas fa-angle-right"></i>
            </button>
            <button 
              className="daire-etf-pagination-btn"
              onClick={() => handleETFPageChange(totalPages)}
              disabled={etfCurrentPage === totalPages}
              title="Last Page"
            >
              <i className="fas fa-angle-double-right"></i>
            </button>
          </div>
        )}
      </div>
    );
  };

  // Copy all the other professional functions from DaireChat...
  const handleStop = () => {
    if (abortController) {
      abortController.abort();
      setAbortController(null);
    }
    setIsProcessing(false);
   
    setStreamingState(prev => ({
      ...prev,
      isStreaming: false,
      isSearching: false,
      isAnalyzing: false,
      documentsFound: 0
    }));
  };

  const handleClearChat = async () => {
    try {
      setMessages([]);
      setShowQuickActionsInternal(true)
      setMessageRatings({});
      setEtfResults([]);
      setShowETFResults(false);
      setEtfCurrentPage(1);
      setSelectedETF(null);
      setEtfSortField('aum');
      setEtfSortDirection('desc');
      setEtfMetrics({
        totalETFs: 0,
        filteredETFs: 0,
        avgTER: 0,
        totalAUM: 0
      });
      setCurrentMessageTokens(null);
      setSessionTotalTokens(0);
      setShouldShowWelcome(true);

      try {
        await clearSessionContext();
      } catch (error) {
        // Session context clearing failed, continue gracefully
      }
    } catch (error) {
      // Chat clearing error handled gracefully
    }
  };

  // Professional markdown components
  const components = {
    code({ node, inline, className, children, ...props }) {
      const match = /language-(\w+)/.exec(className || '');
      return !inline && match ? (
        <SyntaxHighlighter
          style={vscDarkPlus}
          language={match[1]}
          PreTag="div"
          {...props}
        >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      ) : (
        <code className={className} {...props}>
          {children}
        </code>
      );
    }
  };

  // Professional toolbar with Daire branding
  const renderToolbar = () => (
    <div className="chat-toolbar etf-intelligence-toolbar">
      <div className="toolbar-left">
        <div className="etf-daire-brand">
          <div className="etf-daire-icon">
            <img src="/assets/img/daire/daire-logo-icon.png" alt="Daire" className="etf-daire-logo" />
          </div>
          <div className="etf-brand-text-container">
            <span className="etf-daire-brand-text">Daire</span>
            <span className="etf-intelligence-subtitle">ETF Intelligence</span>
          </div>
        </div>
        {currentMessageTokens && (
          <div className="token-usage-display">
            <span className="token-count">{currentMessageTokens.toLocaleString()}</span>
            <span className="token-label">tokens</span>
          </div>
        )}
        {sessionTotalTokens > 0 && (
          <div className="session-token-usage-display">
            <span className="session-token-count">{sessionTotalTokens.toLocaleString()}</span>
            <span className="session-token-label">total</span>
          </div>
        )}
      </div>
      <div className="toolbar-right">
        {!hideFullscreen && (
                  <Tippy content={isFullscreen ? "Exit fullscreen (ESC or F11)" : "Enter fullscreen mode (F11 also works)"}>
          <button onClick={toggleFullscreen} className="toolbar-button fullscreen">
            {isFullscreen ? (
              <FontAwesomeIcon icon={faCompress} />
            ) : (
              <FontAwesomeIcon icon={faExpand} />
            )}
          </button>
        </Tippy>
        )}
        {onShowHelp && (
          <Tippy content="Help">
            <button onClick={onShowHelp} className="toolbar-button">
              <FontAwesomeIcon icon={faQuestionCircle} />
            </button>
          </Tippy>
        )}
        <Tippy content="Clear Chat">
          <button onClick={handleClearChat} className="toolbar-button">
            <FontAwesomeIcon icon={faEraser} />
          </button>
        </Tippy>
        <Tippy content="Close">
          <button onClick={onNavigateHome} className="toolbar-button close-button">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </Tippy>
      </div>
    </div>
  );

  // Fullscreen API integration
  const toggleFullscreen = React.useCallback(() => {
    if (!isFullscreen) {
      // Enter fullscreen - use browser's native fullscreen API
      const targetElement = document.documentElement; // Use entire page for maximum impact!
      if (targetElement.requestFullscreen) {
        targetElement.requestFullscreen();
      } else if (targetElement.webkitRequestFullscreen) {
        targetElement.webkitRequestFullscreen();
      } else if (targetElement.msRequestFullscreen) {
        targetElement.msRequestFullscreen();
      }
      setIsFullscreen(true);
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
      setIsFullscreen(false);
    }
  }, [isFullscreen]);

  // Fullscreen API event handlers 
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement
      );
      
      if (isFullscreen && !isCurrentlyFullscreen) {
        // User exited fullscreen via ESC or other means
        setIsFullscreen(false);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, [isFullscreen]);

  // Keyboard handling for fullscreen (ESC + F11)
  useEffect(() => {
    const handleKeyboard = (e) => {
      if (e.key === 'Escape' && isFullscreen) {
        // Exit fullscreen when ESC is pressed
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen();
        }
        setIsFullscreen(false);
      } else if (e.key === 'F11') {
        // Sync our state with F11 native fullscreen
        e.preventDefault();
        toggleFullscreen();
      }
    };
    
    window.addEventListener('keydown', handleKeyboard);
    return () => window.removeEventListener('keydown', handleKeyboard);
  }, [isFullscreen, toggleFullscreen]);

  // Professional message rendering
  const renderMessage = (message, index) => {
    const isWelcome = index === 0 && message.type === 'bot' && messages.length === 1;
    return (
      <div key={index} className={`message ${message.type}`}>
        {message.type === 'user' ? (
          <div className="user-message-content">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={components}
              className="markdown-content"
            >
              {message.content}
            </ReactMarkdown>
          </div>
        ) : (
          <>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={components}
              className="markdown-content"
            >
              {message.content}
            </ReactMarkdown>
            {!isWelcome && (
              <div className="message-actions">
                <button
                  className={`message-action-button ${copiedMessageId === index ? 'copy-success' : ''}`}
                  onClick={() => handleCopy(index, message.content)}
                >
                  {copiedMessageId === index ? (
                    <>
                      <i className="fas fa-check"></i>
                      Copied!
                    </>
                  ) : (
                    <>
                      <i className="fas fa-copy"></i>
                      Copy
                    </>
                  )}
                </button>
                {message.type === 'bot' && (
                  <div className="rating-buttons">
                    <button
                      className={`rating-button thumbs-up ${messageRatings[index] === 'positive' ? 'active' : ''}`}
                      onClick={() => handleRating(index, 'positive')}
                      title="Thumbs up"
                    >
                      <i className="fas fa-thumbs-up"></i>
                    </button>
                    <button
                      className={`rating-button thumbs-down ${messageRatings[index] === 'negative' ? 'active' : ''}`}
                      onClick={() => handleRating(index, 'negative')}
                      title="Thumbs down"
                    >
                      <i className="fas fa-thumbs-down"></i>
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // Professional copy handler
  const handleCopy = async (messageId, content) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Professional rating handler
  const handleRating = async (messageId, rating) => {
    try {
      const messageToRate = messages[messageId];
      if (!messageToRate) {
        return;
      }

      const listId = messageToRate.listId;
      if (!listId) {
        return;
      }

      await apiHelper.updateChatRating(messageId, rating, user?.profile?.email, listId);
      setMessageRatings(prev => ({
        ...prev,
        [messageId]: rating
      }));
      
      toast.success(`Rating ${rating === 'positive' ? '👍' : '👎'} saved!`, {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (error) {
      // Rating update failed
      toast.error('Failed to save rating');
    }
  };

  // Streaming status component
  const StreamingStatus = ({ state }) => {
    let statusText = '';
    if (state.isSearching) {
      statusText = 'Analyzing ETF data...';
    } else if (state.isAnalyzing) {
      statusText = 'Processing ETF intelligence...';
    } else if (isProcessing) {
      statusText = 'Generating insights...';
    }
    return (
      <div className="streaming-status etf-streaming">
        {(state.isSearching || state.isAnalyzing || isProcessing) && (
          <>
            <div className="typing-indicator etf-processing">
              <div className="typing-dot"></div>
              <div className="typing-dot"></div>
              <div className="typing-dot"></div>
            </div>
            <span className="status-text">{statusText}</span>
          </>
        )}
      </div>
    );
  };

  // Professional scroll handling - delicate auto-scroll 
  // Add scroll handler to detect when user is near bottom
  const handleScroll = () => {
    // Ignore scroll events during auto-scrolling to prevent false detection
    if (isAutoScrollingRef.current) {
      return;
    }
    
    if (chatWindowRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = chatWindowRef.current;
      const atBottom = scrollHeight - scrollTop - clientHeight < 150; // Much more generous threshold for dynamic content
      const wasScrolledUp = userScrolledUp;
      const isScrolledUp = !atBottom;
      
      // Only update if this is a real user scroll action
      if (wasScrolledUp !== isScrolledUp) {
        setUserScrolledUp(isScrolledUp);
      }
    }
  };

  // Add effect to handle scrolling
  useEffect(() => {
    const chatWindow = chatWindowRef.current;
    if (chatWindow) {
      chatWindow.addEventListener('scroll', handleScroll);
      return () => chatWindow.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Before messages update, capture if user is at bottom
  useLayoutEffect(() => {
    if (chatWindowRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = chatWindowRef.current;
      userWasAtBottomRef.current = scrollHeight - scrollTop - clientHeight < 30;
    }
  }, [messages.length]);

  // Update the scroll effect for new messages - respectful auto-scroll
  useEffect(() => {
    // Only auto-scroll if user hasn't manually scrolled up - ALWAYS respect user choice
    if (chatWindowRef.current && !userScrolledUp) {
      isAutoScrollingRef.current = true; // Block scroll detection
      requestAnimationFrame(() => {
        if (chatWindowRef.current) {
          chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
          // Clear the flag after scroll completes
          setTimeout(() => {
            isAutoScrollingRef.current = false;
          }, 100);
        }
      });
    }
  }, [messages, userScrolledUp]);

  // Also scroll when ETF results update - but respect user scroll preference and only when shown
  useEffect(() => {
    if (showETFResults && etfResults.length > 0 && !userScrolledUp) {
      isAutoScrollingRef.current = true; // Block scroll detection
      requestAnimationFrame(() => {
        if (chatWindowRef.current) {
          chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
          // Clear the flag after scroll completes
          setTimeout(() => {
            isAutoScrollingRef.current = false;
          }, 100);
        }
      });
    }
  }, [showETFResults, userScrolledUp]); // Remove etfResults dependency to prevent auto-show

  // Scroll to bottom button functionality
  const scrollToBottom = () => {
    if (chatWindowRef.current) {
      isAutoScrollingRef.current = true; // Block scroll detection
      chatWindowRef.current.scrollTo({
        top: chatWindowRef.current.scrollHeight,
        behavior: 'smooth'
      });
      setUserScrolledUp(false);
      userWasAtBottomRef.current = true;
      // Clear the flag after smooth scroll completes
      setTimeout(() => {
        isAutoScrollingRef.current = false;
      }, 300); // Longer timeout for smooth scroll
    }
  };

  // ETF results toggle functionality
  const handleETFResultsToggle = () => {
    setShowETFResults(!showETFResults);
    // If we're showing results, wait for state update and then scroll
    if (!showETFResults) {
      setTimeout(() => {
        if (chatWindowRef.current && !userScrolledUp) {
          chatWindowRef.current.scrollTo({
            top: chatWindowRef.current.scrollHeight,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  };

  // Helper to create a signature for the ETF results array
  const getETFResultsSignature = (results) => {
    if (!results || results.length === 0) return '';
    return results.slice(0, 5).map(r => r.fundName + r.fundTER).join('|');
  };

  // Flash the Show ETF Results button when new results are loaded
  useEffect(() => {
    const signature = getETFResultsSignature(etfResults);
    if (signature && signature !== lastETFResultsSignature.current) {
      setFlashETFResultsButton(false); // Reset in case it's still true
      setTimeout(() => setFlashETFResultsButton(true), 10); // Small delay to retrigger animation
      setTimeout(() => setFlashETFResultsButton(false), 3010); // Match the animation duration
      lastETFResultsSignature.current = signature;
    }
  }, [etfResults]);

  return (
    <div className={`daira-chat etf-intelligence-chat${isOnDairePage ? ' is-on-daire-page' : ''}${!isOnDairePage ? ' daire-sidebar' : ''}${isFullscreen ? ' fullscreen-mode nuclear-fullscreen' : ''}`}>
      <ToastContainer />
      {renderToolbar()}
      <div className={`chat-window${isFullscreen ? ' fullscreen-chat-window nuclear-chat-window' : ''}`} ref={chatWindowRef}>
        {messages.map((message, index) => renderMessage(message, index))}
        {showETFResults && <ETFResultsDisplay />}
        <div ref={bottomRef} />
        {/* Scroll to bottom button - appears when user scrolled up */}
        {userScrolledUp && (
          <button
            className="scroll-bottom-button etf-scroll-button"
            onClick={scrollToBottom}
            title="Scroll to bottom"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 13l-7 7-7-7m14-8l-7 7-7-7" />
            </svg>
          </button>
        )}
        {/* {!isProcessing && showQuickActionsInternal && <ETFQuickActions />} */}
        <StreamingStatus state={streamingState} />
      </div>
      <form onSubmit={handleSubmit} className="input-form etf-input-form">
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            if (e.target.value) {
              setShowQuickActionsInternal(false);
            }
          }}
          placeholder="Ask about ETFs..."
          disabled={isProcessing}
        />
        {etfResults.length > 0 && !isProcessing && (
          <button
            type="button"
            onClick={handleETFResultsToggle}
            className={`etf-results-button${showETFResults ? ' active' : ''}${flashETFResultsButton ? ' flash' : ''}`}
            title={showETFResults ? "Hide ETF Results" : "Show ETF Results"}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" />
            </svg>
          </button>
        )}
        <button
          type="submit"
          disabled={isProcessing || !input.trim()}
          className={`send-button etf-send-button ${isProcessing ? 'processing' : ''}`}
        >
          {isProcessing ? (
            <div className="stop-button" onClick={handleStop}>
              <FontAwesomeIcon icon={faStop} />
            </div>
          ) : (
            <FontAwesomeIcon icon={faBolt} />
          )}
        </button>
      </form>

      {!isProcessing && messages.length > 1 && messages.some(msg => msg.type === 'bot') && (
                          <div className="etf-disclaimer" key="etf-disclaimer">
          <FontAwesomeIcon icon={faExclamationCircle} className="disclaimer-icon" title="Warning: AI-generated content may contain inaccuracies" />
          <span className="etf-disclaimer-text">
            ETF Intelligence responses are AI-generated • Always verify fund information • Always seek professional advice
          </span>
        
            <div className="data-source-attribution">
            <img src="/assets/img/etfbook.png" alt="ETF Book" className="etfbook-logo" title="Data powered by ETF Book" style={{ cursor: 'help' }} />
          </div>
        </div>
      )}

      {/* ETF Details Modal */}
      {selectedETF && (
        <div className="daire-etf-modal-overlay" onClick={closeETFModal}>
          <div className="daire-etf-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="daire-etf-modal-header">
              <h3 className="daire-etf-modal-title">{selectedETF.fundName}</h3>
              <button className="daire-etf-modal-close" onClick={closeETFModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div className="daire-etf-modal-body">
              <div className="daire-etf-detail-grid">
                <div className="daire-etf-detail-item">
                  <span className="daire-detail-label">ISIN:</span>
                  <span className="daire-detail-value">
                    <code>{selectedETF.fundISIN}</code>
                  </span>
                </div>
                
                <div className="daire-etf-detail-item">
                  <span className="daire-detail-label">TER:</span>
                  <span className="daire-detail-value ter-value">{formatTER(selectedETF.fundTER)}</span>
                </div>
                
                <div className="daire-etf-detail-item">
                  <span className="daire-detail-label">AUM:</span>
                  <span className="daire-detail-value aum-value">{formatAUM(selectedETF.aum)}</span>
                </div>
                
                <div className="daire-etf-detail-item">
                  <span className="daire-detail-label">Fund Domicile:</span>
                  <span className="daire-detail-value">{selectedETF.fundDomicile}</span>
                </div>
               
                <div className="daire-etf-detail-item">
                  <span className="daire-detail-label">Asset Class:</span>
                  <span className="daire-detail-value">{selectedETF.fundAssetClass}</span>
                </div>
                
                <div className="daire-etf-detail-item">
                  <span className="daire-detail-label">Geographic Focus:</span>
                  <span className="daire-detail-value">{selectedETF.fundAssetClassGeoFocus}</span>
                </div>
                
                <div className="daire-etf-detail-item">
                  <span className="daire-detail-label">Currency:</span>
                  <span className="daire-detail-value">{selectedETF.fundCurrency}</span>
                </div>
               
                {selectedETF.aumAsOfDate && (
                  <div className="daire-etf-detail-item">
                    <span className="daire-detail-label">AUM As Of Date:</span>
                    <span className="daire-detail-value">{formatDateIrish(selectedETF.aumAsOfDate)}</span>
                  </div>
                )}
                
                <div className="daire-etf-detail-item">
                  <span className="daire-detail-label">ESG:</span>
                  <span className="daire-detail-value">
                    {selectedETF.fundESG === 'YES' ? (
                      <span className="daire-esg-badge-modal">{selectedETF.fundESGCategory || 'Yes'}</span>
                    ) : (
                      'No'
                    )}
                  </span>
                </div>
                
                <div className="daire-etf-detail-item">
                  <span className="daire-detail-label">SFDR Article:</span>
                  <span className="daire-detail-value">
                    <span className="daire-sfdr-badge-modal">{formatSFDR(selectedETF.fundSFDR)}</span>
                  </span>
                </div>
                

              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default ETFDaireChat; 